import { useQuery } from '@tanstack/react-query'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { analyticsApi } from 'src/services/analyticsApi'
import {
  BackendSentiment,
  CampaignComment,
  SocialCommentApiItem,
  toChartComment,
} from 'src/reactQuery/useGetCampaignCommentsQuery'

// GET /v1_private/analytics/fetchThreadDetails — one mentioned post with its
// comments (sm.back modules/socialComments.js). Types written by hand from
// src/schema/swagger/v1_private/analytics/fetchThreadDetails.yml.

export interface CommentedPost {
  _id: string
  account: string | null
  author: string | null
  campaign: string
  campaign_name: string | null
  canonical_link: string | null
  commentsBreakdown: { negative: number; neutral: number; positive: number }
  commentsCount: number
  irrelevant: boolean
  keyword: string | null
  language: string
  link: string | null
  negative_keywords: string[]
  original_lang_code: string | null
  output: BackendSentiment | null
  positive_keywords: string[]
  published_at: string | null
  reactions: number | null
  /** Instagram only: the repost count, separate from shares; null = not reported. */
  reposts: number | null
  shares: number | null
  social_media_mention_type: string | null
  source: string | null
  /** News and websites: the outlet ("BMG"). */
  source_name?: string | null
  text: string
  translated: string | null
  views: number | null
}

export interface ThreadDetailsApiResponse {
  data: {
    comments: SocialCommentApiItem[]
    post: CommentedPost
    totalComments: number
    truncated: boolean
  }
  success: boolean
}

export interface ThreadDetailsData {
  comments: CampaignComment[]
  post: CommentedPost
  totalComments: number
  truncated: boolean
}

export const useGetThreadDetailsQuery = (
  threadId: string | null | undefined,
  options?: { enabled?: boolean }
) => {
  return useQuery<ThreadDetailsData>({
    enabled: (options?.enabled ?? true) && !!threadId,
    queryFn: async () => {
      const response = await analyticsApi().get<ThreadDetailsApiResponse>(
        '/v1_private/analytics/fetchThreadDetails',
        { params: { threadId } }
      )
      const data = response.data.data
      return {
        ...data,
        comments: (data.comments || []).map(toChartComment),
      }
    },
    queryKey: [QueryKey.GET_THREAD_DETAILS, threadId],
    staleTime: 60 * 1000,
  })
}
